import {
  Customer,
  InputRuleApiModel,
  RequiredIfCondition,
  RequiredIfGroupCondition,
  RequiredIfModel
} from '~/lib/api/smartsend/types';
import sanitiseValue from '~/lib/utils/value-sanitiser';

// Mirrors the semantics of ui-checkout's evaluateFieldComparison in src/lib/utils/requirements.tsx.
const Check = (
  c: RequiredIfCondition,
  primaryCustomerType: 'Sender' | 'Recipient',
  customer: Customer,
  secondaryCustomer?: Customer
): boolean => {
  // Field includes Sender or Recipient explicitly.
  const model = c.field?.includes('.') ? c.field.split('.')[0] : primaryCustomerType;

  const customerToUse = model === primaryCustomerType ? customer : secondaryCustomer;

  if (!customerToUse) {
    return false;
  }

  const field = c.field?.includes('.') ? c.field.split('.')[1] : c.field;

  const fieldToCheckValue: string = customerToUse.Meta[field] ? customerToUse.Meta[field]?.Value : customerToUse[field];

  const compareOp = c.compare ?? 'Eq';

  if (c.value === null) {
    const isEmpty = fieldToCheckValue === null || fieldToCheckValue === undefined || fieldToCheckValue === '';
    return compareOp === 'Eq' ? isEmpty : !isEmpty;
  }

  const matches = Array.isArray(c.value)
    ? c.value.some((v) => sanitiseValue(fieldToCheckValue) === sanitiseValue(v))
    : sanitiseValue(fieldToCheckValue) === sanitiseValue(c.value);

  return compareOp === 'Eq' ? matches : !matches;
};

// Some upstream payloads share a single "value" across every entry in "all"/"any" rather than
// repeating it per item. Mirrors ui-checkout's `e.value !== undefined ? e.value : req.value` fallback
// in src/lib/utils/requirements.tsx. Returns null when neither the entry nor the group has a value.
const resolveGroupCondition = (
  c: RequiredIfGroupCondition,
  sharedValue: RequiredIfCondition['value'] | undefined
): RequiredIfCondition | null => {
  const value = c.value !== undefined ? c.value : sharedValue;
  return value === undefined ? null : { ...c, value };
};

const conditionalIsRequired = (
  m: InputRuleApiModel,
  primaryCustomerType: 'Sender' | 'Recipient',
  customer: Customer,
  secondaryCustomer?: Customer
) => {
  if (!m?.dataRequiredIf) {
    return undefined;
  }

  const conditional =
    typeof m.dataRequiredIf === 'string' ? (JSON.parse(m.dataRequiredIf) as RequiredIfModel) : m.dataRequiredIf;

  if (conditional?.all) {
    return conditional.all.every((c) => {
      const resolved = resolveGroupCondition(c, conditional.value);
      return resolved !== null && Check(resolved, primaryCustomerType, customer, secondaryCustomer);
    });
  }

  if (conditional?.any) {
    return conditional.any.some((c) => {
      const resolved = resolveGroupCondition(c, conditional.value);
      return resolved !== null && Check(resolved, primaryCustomerType, customer, secondaryCustomer);
    });
  }

  if (conditional?.field) {
    return Check(conditional as RequiredIfCondition, primaryCustomerType, customer, secondaryCustomer);
  }

  return undefined;
};

export default conditionalIsRequired;
